import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, LogOut, Clock, MapPin, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth', { state: { from: { pathname: '/dashboard' } } });
    } else if (user) {
      fetchBookings();
    }
  }, [user, authLoading, navigate]);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;

    try {
      await axios.put(`${API}/bookings/${bookingId}/cancel`);
      toast.success('Booking cancelled');
      fetchBookings();
    } catch (error) {
      toast.error('Failed to cancel booking');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'text-green-500';
      case 'pending': return 'text-yellow-500';
      case 'cancelled': return 'text-red-500';
      default: return 'text-foreground/60';
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="user-dashboard">
      <div className="max-w-6xl mx-auto container-padding section-padding">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
          <div>
            <span className="overline">Dashboard</span>
            <h1 className="text-3xl md:text-4xl font-heading">
              Welcome, <span className="text-gold-500">{user?.name}</span>
            </h1>
          </div>
          <button
            onClick={handleLogout}
            className="btn-ghost flex items-center gap-2"
            data-testid="dashboard-logout"
          >
            <LogOut size={18} />
            Logout
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="card-glass p-6 space-y-4">
              <div className="w-16 h-16 bg-gold-500/20 rounded-full flex items-center justify-center mx-auto">
                <User className="text-gold-500" size={32} />
              </div>
              <div className="text-center">
                <h3 className="font-heading text-xl">{user?.name}</h3>
                <p className="text-foreground/60 text-sm">{user?.email}</p>
                {user?.phone && (
                  <p className="text-foreground/60 text-sm">{user?.phone}</p>
                )}
              </div>
              <div className="pt-4 border-t border-gold-500/10">
                <Link to="/book" className="btn-primary w-full text-center block">
                  New Booking
                </Link>
              </div>
            </div>
          </div>

          {/* Bookings */}
          <div className="lg:col-span-3">
            <h2 className="font-heading text-2xl mb-6">Your <span className="text-gold-500">Bookings</span></h2>
            
            {bookings.length === 0 ? (
              <div className="card-glass p-12 text-center">
                <Calendar className="mx-auto mb-4 text-gold-500/50" size={48} />
                <h3 className="font-heading text-xl mb-2">No Bookings Yet</h3>
                <p className="text-foreground/60 mb-6">Start your desert adventure by making a reservation.</p>
                <Link to="/book" className="btn-outline">Book Now</Link>
              </div>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking, index) => (
                  <motion.div
                    key={booking.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="card-glass p-6"
                    data-testid={`booking-${booking.id}`}
                  >
                    <div className="flex flex-col md:flex-row justify-between gap-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-3">
                          <h3 className="font-heading text-xl">{booking.room_name}</h3>
                          <span className={`text-xs uppercase tracking-wider ${getStatusColor(booking.status)}`}>
                            {booking.status}
                          </span>
                        </div>
                        
                        <div className="flex flex-wrap gap-4 text-sm text-foreground/60">
                          <div className="flex items-center gap-2">
                            <Calendar size={14} className="text-gold-500" />
                            <span>{booking.check_in} → {booking.check_out}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <User size={14} className="text-gold-500" />
                            <span>{booking.guests} guests</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock size={14} className="text-gold-500" />
                            <span>Booked {new Date(booking.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-gold-500 font-heading text-xl">₹{booking.total_amount.toLocaleString()}</p>
                          <p className={`text-xs ${booking.payment_status === 'paid' ? 'text-green-500' : 'text-yellow-500'}`}>
                            {booking.payment_status}
                          </p>
                        </div>
                        
                        {booking.status !== 'cancelled' && booking.status !== 'completed' && (
                          <button
                            onClick={() => handleCancelBooking(booking.id)}
                            className="p-2 text-red-500 hover:bg-red-500/10 transition-colors"
                            title="Cancel Booking"
                            data-testid={`cancel-booking-${booking.id}`}
                          >
                            <XCircle size={20} />
                          </button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
